// BecomeTutor.js
import React from "react";
import {
  ArrowRight,
  BadgeCheck,
  ShieldCheck,
  Sparkles,
  Zap,
  Target,
} from "lucide-react";

export default function BecomeTutor() {
  return (
    <section className="relative w-full overflow-hidden py-20">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#25d976]/5 to-transparent" />
      <div className="absolute top-1/4 left-0 w-96 h-96 bg-[#25d976]/20 rounded-full blur-3xl animate-pulse" />
      <div
        className="absolute bottom-1/4 right-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse"
        style={{ animationDelay: "1.5s" }}
      />

      <div className=" relative z-10 mx-auto max-w-6xl px-4">
        {/* Main Card */}
        <div className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900/95 to-slate-800/90 backdrop-blur-xl border border-slate-700/50 shadow-2xl hover:border-[#25d976]/50 transition-all duration-500">
          {/* Animated gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#25d976]/10 via-transparent to-cyan-500/10 opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-[#25d976]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

          {/* Decorative blurs */}
          <div className="absolute -right-32 -top-32 h-64 w-64 rounded-full bg-[#25d976]/30 blur-3xl" />
          <div className="absolute -left-32 -bottom-32 h-64 w-64 rounded-full bg-cyan-500/20 blur-3xl" />

          <div className="baloo-da-2-tutor relative z-10 grid gap-8 p-8 sm:p-12 lg:grid-cols-2 lg:items-center lg:gap-12">
            {/* Left: Content */}
            <div className="space-y-6">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 rounded-full border border-[#25d976]/30 bg-[#25d976]/10 backdrop-blur-sm px-4 py-2 shadow-lg shadow-[#25d976]/20">
                <Sparkles className="h-4 w-4 text-[#25d976] animate-pulse" />
                <span className="text-sm font-bold tracking-wide text-[#25d976]">
                  টিউটরদের জন্য বিশেষ
                </span>
              </div>

              {/* Description */}
              <p className="text-base leading-relaxed text-slate-300 sm:text-lg">
                সরাসরি স্টুডেন্টদের সাথে যোগাযোগ করো।
              </p>

              {/* Benefits Grid */}
              <div className="grid gap-4 sm:grid-cols-2">
                <Benefit
                  icon={BadgeCheck}
                  title="ইউনিভার্সিটি ভেরিফাইড ব্যাজ"
                  desc="বিশ্বাস বাড়ায়, সিলেকশন দ্রুত হয়।"
                />
                <Benefit
                  icon={ShieldCheck}
                  title="ট্রান্সপারেন্ট"
                  desc="ফেক পোস্ট/স্প্যাম কমানোর চেষ্টা।"
                />
                {/* <Benefit
                  icon={Zap}
                  title="তাৎক্ষণিক নোটিফিকেশন"
                  desc="নতুন টিউশন পোস্ট হলেই জানো।"
                /> */}
                <Benefit
                  icon={Target}
                  title="স্মার্ট ম্যাচিং সিস্টেম"
                  desc="তোমার পছন্দ অনুযায়ী ফিল্টার।"
                />
              </div>

              {/* CTAs */}
              <div className="flex flex-col gap-4 pt-2 sm:flex-row sm:items-center">
                <a
                  href="/tutor-submission"
                  className="group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-2xl bg-gradient-to-r from-[#25d976] to-emerald-500 px-8 py-4 text-base font-bold text-slate-900 shadow-lg shadow-[#25d976]/50 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#25d976]/60 active:scale-100"
                >
                  <span className="relative z-10">Become a Tutor</span>
                  <ArrowRight className="relative z-10 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />

                  {/* Shimmer effect */}
                  <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                </a>

                <a
                  href="/tuition-board"
                  className="inline-flex items-center justify-center rounded-2xl border-2 border-[#25d976]/50 bg-transparent px-8 py-4 text-base font-bold text-[#25d976] backdrop-blur-sm transition-all duration-300 hover:bg-[#25d976]/10 hover:border-[#25d976] hover:scale-105 active:scale-100"
                >
                  Find Tuitions
                </a>
              </div>

              {/* Trust note */}
              {/* <div className="flex items-start gap-3 rounded-xl border border-slate-700/50 bg-slate-800/50 p-4 backdrop-blur-sm">
                <div className="mt-0.5 flex-shrink-0">
                  <div className="h-2 w-2 rounded-full bg-[#25d976] animate-pulse" />
                </div>
                <p className="text-xs leading-relaxed text-slate-400">
                  রেজিস্ট্রেশন করে আপনি আপনার প্রোফাইল আপডেট, টিউশন ফিল্টার, এবং
                  সুযোগগুলো দ্রুত দেখতে পারবেন।
                </p>
              </div> */}
            </div>

            {/* Right: Image Section */}
            <div className="relative lg:pl-4">
              <div className="relative overflow-hidden rounded-2xl border border-slate-700/50 bg-slate-800/50 backdrop-blur-sm shadow-2xl">
                {/* Image */}
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src="https://goatteachers.com/assets/CTA_Pic.png"
                    alt="Tutor teaching"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />

                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />

                  {/* Stats overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 md:p-6">
                    <div className="grid grid-cols-3 gap-1.5 sm:gap-2 md:gap-3">
                      <StatPill
                        value="Fast"
                        gradient="from-cyan-500 to-blue-500"
                        className="px-2 py-1 sm:px-3 sm:py-1.5"
                        textClassName="text-[10px] sm:text-xs md:text-sm whitespace-nowrap"
                      />
                      <StatPill
                        value="Smart"
                        gradient="from-[#25d976] to-emerald-500"
                        className="px-2 py-1 sm:px-3 sm:py-1.5"
                        textClassName="text-[10px] sm:text-xs md:text-sm whitespace-nowrap"
                      />
                      <StatPill
                        value="Clean"
                        gradient="from-violet-500 to-purple-500"
                        className="px-2 py-1 sm:px-3 sm:py-1.5"
                        textClassName="text-[10px] sm:text-xs md:text-sm whitespace-nowrap"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -bottom-6 -left-6 h-24 w-24 rounded-full bg-[#25d976]/20 blur-2xl" />
              <div className="absolute -top-6 -right-6 h-24 w-24 rounded-full bg-cyan-500/20 blur-2xl" />
            </div>
          </div>

          {/* Bottom decorative line */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#25d976] to-transparent opacity-50" />
        </div>
      </div>
    </section>
  );
}

function Benefit({ icon: Icon, title, desc }) {
  return (
    <div className="group/benefit relative overflow-hidden rounded-2xl border border-slate-700/50 bg-slate-800/50 p-4 backdrop-blur-sm transition-all duration-300 hover:border-[#25d976]/50 hover:bg-slate-800/70">
      {/* Hover glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#25d976]/0 to-[#25d976]/10 opacity-0 transition-opacity duration-300 group-hover/benefit:opacity-100" />

      <div className="relative flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br from-[#25d976]/20 to-[#25d976]/5 border border-[#25d976]/30 transition-transform duration-300 group-hover/benefit:scale-110 group-hover/benefit:rotate-3">
            <Icon className="h-6 w-6 text-[#25d976]" />
          </div>
        </div>

        {/* Text */}
        <div className="flex-1 pt-1">
          <div className="text-sm font-bold text-white mb-1 group-hover/benefit:text-[#25d976] transition-colors duration-200">
            {title}
          </div>
          <div className="text-xs leading-relaxed text-slate-400">{desc}</div>
        </div>
      </div>
    </div>
  );
}

const StatPill = ({
  value,
  label,
  gradient,
  className = "",
  textClassName = "",
}) => (
  <div
    className={`rounded-xl bg-gradient-to-r ${gradient} px-3 py-1.5 text-center ${className}`}
  >
    <div className={`font-semibold text-white leading-none ${textClassName}`}>
      {value}
    </div>
    {label ? (
      <div className="mt-0.5 text-[10px] text-white/80 leading-none">
        {label}
      </div>
    ) : null}
  </div>
);
